import { useState } from 'react'

import { useResetVorschau, useTripReset } from '../../hooks/queries'
import { useOnline } from '../../hooks/useOutbox'
import { groesse } from '../../lib/format'

/** „1 Nachricht" / „3 Nachrichten" — die Pluralregel steht an einer Stelle. */
function worte(anzahl: number, eins: string, viele: string): string {
  return `${anzahl} ${anzahl === 1 ? eins : viele}`
}

/**
 * Den Inhalt einer Reise wegwerfen und die Reise behalten (#12).
 *
 * Aus dem Issue: „Eine Reise wird angelegt, kurz zu zweit ausprobiert — und
 * danach soll das Probegeschreibsel weg sein, bevor die eigentlichen Empfänger
 * dazukommen." Es ist ein **Zurücksetzen, kein Löschen**.
 *
 * **Zwei Schritte, und der erste ist keine Zierde.** Erst steht da, was genau
 * verschwindet — gezählt vom Server, nicht geraten —, dann muss ein Code
 * abgetippt werden. Beides zusammen ist die Rückfrage; eine gewöhnliche
 * „Wirklich?"-Frage wäre hier zu wenig, weil es nichts zurückzuholen gibt.
 *
 * **Der Code ist eine Bestätigung, kein Geheimnis.** Wer das hier überhaupt
 * sieht, ist der Inhaber der Reise und dürfte auch ohne. Er soll verhindern,
 * dass jemand seinen Bericht mit einem Fehlgriff wegwischt.
 */
export function Zuruecksetzen({ tripId, titel }: { tripId: number; titel: string }) {
  const [offen, setOffen] = useState(false)
  const [getippt, setGetippt] = useState('')
  const [befund, setBefund] = useState<string | null>(null)
  const vorschau = useResetVorschau(tripId, offen)
  const reset = useTripReset(tripId)
  const online = useOnline()

  const zahlen = vorschau.data
  const passt = zahlen !== undefined && getippt.trim().toUpperCase() === zahlen.code

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Chat zurücksetzen</h2>

      <p className="feld__hinweis feld__hinweis--13">
        Wirft den ganzen Inhalt dieser Reise weg: Nachrichten mit Fotos und
        Videos, Reaktionen, Lesestände, die Route und alle Punkte auf der Karte.
        Die Reise selbst bleibt — mit ihren Empfängern, Einladungen,
        Einstellungen, dem Symbolbild und dem Chat-Hintergrund.
      </p>
      <p className="feld__hinweis feld__hinweis--13">
        Gedacht für die Probe zu zweit, bevor die eigentlichen Empfänger
        dazukommen. <strong>Rückgängig geht das nicht.</strong>
      </p>

      {!offen && (
        <button
          type="button"
          className="button button--outline button--gefahr"
          onClick={() => {
            setGetippt('')
            setBefund(null)
            setOffen(true)
          }}
        >
          Zurücksetzen …
        </button>
      )}

      {offen && (
        <div className="stack" style={{ gap: 12 }}>
          {vorschau.isPending && <p className="feld__hinweis">Wird gezählt …</p>}

          {vorschau.isError && (
            <p className="notice" role="alert">
              {vorschau.error instanceof Error
                ? vorschau.error.message
                : 'Die Übersicht lässt sich gerade nicht laden.'}
            </p>
          )}

          {zahlen !== undefined && (
            <>
              {/* **Was verschwindet, in Zahlen.** Erst danach kommt das Feld:
                  wer nicht gelesen hat, was es kostet, soll nicht tippen
                  können. */}
              <div>
                <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Das verschwindet:</p>
                <ul className="feld__hinweis feld__hinweis--13" style={{ margin: 0, paddingLeft: 20 }}>
                  <li>{worte(zahlen.messages, 'Nachricht', 'Nachrichten')}</li>
                  <li>
                    {worte(zahlen.media, 'Foto oder Video', 'Fotos und Videos')}
                    {zahlen.bytes > 0 && ` · ${groesse(zahlen.bytes)}`}
                  </li>
                  <li>{worte(zahlen.tracks, 'GPX-Datei', 'GPX-Dateien')}</li>
                  <li>{worte(zahlen.highlights, 'Punkt auf der Karte', 'Punkte auf der Karte')}</li>
                </ul>
              </div>

              {/* **Wer mitliest, gehört gesagt.** Zurücksetzen geht auch mit
                  Empfängern — dann trifft es nicht nur das eigene
                  Probegeschreibsel, sondern auch deren Antworten. */}
              {zahlen.readers > 0 && (
                <p className="notice" role="alert">
                  {worte(zahlen.readers, 'Person liest', 'Personen lesen')} diese Reise mit.
                  Auch ihre Antworten verschwinden.
                </p>
              )}

              <label className="field">
                <span className="field__label">
                  Zum Bestätigen abtippen: <strong>{zahlen.code}</strong>
                </span>
                <input
                  className="field__input"
                  value={getippt}
                  onChange={(event) => setGetippt(event.target.value)}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-label={`Code ${zahlen.code} abtippen`}
                  placeholder={zahlen.code}
                />
              </label>

              {befund !== null && (
                <p className="feld__hinweis" role="status">
                  {befund}
                </p>
              )}

              {reset.isError && (
                <p className="notice" role="alert">
                  {reset.error instanceof Error ? reset.error.message : 'Das hat nicht geklappt.'}
                </p>
              )}

              {/* Ohne Netz abgeschaltet, mit Grund als Text — ein Tooltip ist
                  auf dem Telefon unsichtbar. */}
              {!online && (
                <p className="feld__hinweis" role="alert">
                  Ohne Netz geht das gerade nicht.
                </p>
              )}

              <div className="row" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="button button--outline button--small"
                  onClick={() => setOffen(false)}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="button button--outline button--small button--gefahr"
                  disabled={!passt || !online || reset.isPending}
                  onClick={async () => {
                    const weg = await reset.mutateAsync(getippt)
                    setOffen(false)
                    setGetippt('')
                    // **Gesagt wird, was wirklich wegging** -- die Zahlen der
                    // Antwort und nicht die der Vorschau davor.
                    setBefund(
                      `„${titel}" ist zurückgesetzt: ${worte(weg.messages, 'Nachricht', 'Nachrichten')}, `
                        + `${worte(weg.media, 'Datei', 'Dateien')}, `
                        + `${worte(weg.tracks, 'GPX-Datei', 'GPX-Dateien')} und `
                        + `${worte(weg.highlights, 'Punkt', 'Punkte')} sind weg.`,
                    )
                  }}
                >
                  {reset.isPending ? 'Wird zurückgesetzt …' : 'Endgültig zurücksetzen'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Der Befund bleibt stehen, wenn der Kasten wieder zu ist -- sonst
          passierte etwas und niemand sähe es. */}
      {!offen && befund !== null && (
        <p className="feld__hinweis" role="status">
          {befund}
        </p>
      )}
    </div>
  )
}
